<template>
  <div class="therapist-search sanitasForms">
    <p id="list-date">{{ $t('list-date-text', { date: listDate }) }}</p>
    <alert-message id="alert-text" :text="$t('form-info-text')" />
    <drop-down
      v-model="searchParams.method"
      :title-label="$t('method-dropdown-title')"
      :options="methodDropdownItems"
    >
    </drop-down>
    <text-input
      v-model="searchParams.name"
      :title-label="$t('therapist-name-field-title')"
    ></text-input>
    <sanitas-zip-chooser
      v-model="searchParams.zipOrCity"
      class="margin-bottom"
      :title="$t('address-field-title')"
      info-message-no-result=""
      :data-function="getLocation"
    />
    <radio-group
      v-model="searchParams.radius"
      :items="formattedItems"
      class="margin-bottom"
      required
      horizontal
    />
    <form-buttons>
      <sanitas-button secondary @click="userDidPressCancel">
        {{ $t('cancel-button-title') }}
      </sanitas-button>
      <sanitas-button id="search-button" primary @click="startTheSearch(0)">
        {{ $t('search-button-title') }}
      </sanitas-button>
    </form-buttons>

    <template v-if="shouldShowNoResultsFound">
      <p id="no-results-found-text">{{ $t('no-results-found-text') }}</p>
    </template>
    <template v-if="showErrorMessage">
      <p id="error-text">{{ $t('error-text') }}</p>
    </template>

    <div :class="resultsClass">
      <therapist-search-table :results="results" />
      <pagination
        :total-pages="totalSearchPages"
        :current-page="currentSearchPage"
        @goToPage="goToPage"
        @goToPrevPage="goToPage(currentSearchPage - 1)"
        @goToNextPage="goToPage(currentSearchPage + 1)"
      />
    </div>
  </div>
</template>

<script>
import { provideI36n, useI36n } from '@jota-one/i36n/src/index.vue2'

import FormButtons from '@/components/FormButtons'
import { client } from '@/helpers/http'
import { setTrackerVersion, trackAction, tracker } from '@/helpers/tracker'

import AlertMessage from './components/AlertMessage'
import Pagination from './components/Pagination'
import TherapistSearchTable from './components/TherapistSearchTable'

export default {
  name: 'SanitasTherapistSearch',
  components: {
    FormButtons,
    Pagination,
    TherapistSearchTable,
    AlertMessage,
    DropDown: () => {
      return import(
        /* webpackChunkName: "sanitas-components" */
        '@/components/sanitas-components/DropDown'
      )
    },
    TextInput: () => {
      return import(
        /* webpackChunkName: "sanitas-components" */
        '@/components/sanitas-components/TextInput'
      )
    },
    RadioGroup: () => {
      return import(
        /* webpackChunkName: "sanitas-components" */
        '@/components/sanitas-components/RadioGroup'
      )
    },
    SanitasButton: () => {
      return import(
        /* webpackChunkName: "sanitas-components" */
        '@/components/sanitas-components/SanitasButton'
      )
    },
    SanitasZipChooser: () => {
      return import(
        /* webpackChunkName: "sanitas-components" */
        '@/components/sanitas-components/SanitasZipChooser'
      )
    },
  },

  props: {
    /**
     * endpoint used to retrieve the search results
     */
    searchEndpoint: {
      type: String,
      required: true,
    },

    /**
     * the max number of elements retrieved per each page search
     */
    numberOfElementsPerPage: {
      type: Number,
      default: 10,
    },

    listDate: {
      type: String,
      required: true,
    },

    /**
     *  list of methods displayed in the therapy methods dropdown
     */
    methodDropdownItems: {
      type: Array,
      default: () => [],
    },

    /**
     *  items/options to display in the distance checkbox group
     */
    distanceFieldItems: {
      type: Array,
      default: () => [
        { value: 0, name: 'Place' },
        { value: 5, name: '5Km' },
      ],
    },

    /**
     * search results shown in the table
     */
    resultTableItems: {
      type: Array,
      default: () => [],
    },
  },

  setup() {
    const language = window.Sanitas.language || 'de'
    const loadLabels = lang => import(`./i18n/${lang}.json`)
    provideI36n(language, { load: loadLabels })
    const { $label } = useI36n()

    return {
      $t: $label,
    }
  },
  data: () => ({
    currentSearchPage: 5,
    totalSearchPages: 6,
    showErrorMessage: false,
    userHasSearched: false,
    searchParams: {
      method: undefined,
      name: undefined,
      radius: undefined,
      zipOrCity: undefined,
    },
    results: [],
  }),
  computed: {
    shouldShowNoResultsFound() {
      return this.userHasSearched && this.results.length === 0
    },
    resultsClass() {
      return this.userHasSearched && this.results.length > 0 ? '' : 'is-hidden'
    },
    formattedItems() {
      return this.distanceFieldItems.map(el => {
        return {
          text: el.name,
          value: el.value,
        }
      })
    },
  },

  created() {
    setTrackerVersion()

    this.results =
      this.resultTableItems && this.resultTableItems.length > 0
        ? this.resultTableItems
        : []
    this.searchParams.radius = this.distanceFieldItems[0].value
  },
  methods: {
    goToPage(page) {
      if (
        page !== this.currentSearchPage &&
        page >= 0 &&
        page < this.totalSearchPages
      ) {
        this.startTheSearch(page)
      }
    },
    startTheSearch(page = 0) {
      const searchParams = this.prepareSearchParamsForPage(page + 1)

      const numberOfRequiredElements = 4
      const numberOfFilledElements = Object.keys(searchParams).length

      if (numberOfFilledElements < numberOfRequiredElements) {
        this.showErrorMessage = true
      } else {
        this.userHasSearched = false
        this.currentSearchPage = page
        this.showErrorMessage = false

        const params = Object.keys(searchParams)
          .map(key => key + '=' + encodeURIComponent(searchParams[key]))
          .join('&')

        client()
          .get(this.searchEndpoint + '?' + params)
          .then(({ data }) => {
            this.results = data.therapists
            this.totalSearchPages = Math.ceil(
              data.totalItems / this.numberOfElementsPerPage,
            )
            this.userHasSearched = true
            tracker.set({
              data: {
                search: {
                  formId: 'therapist',
                  query: '',
                  resultsCount: this.results.length,
                },
              },
            })
            trackAction('ComSearchSubmitted')
          })
          .catch(err => {
            console.log(err)
            this.results = []
            this.userHasSearched = true
            tracker.set({
              data: {
                search: { formId: 'therapist', query: '', resultsCount: 0 },
              },
            })
            trackAction('ComSearchSubmittedNoResult')
          })
      }
    },
    prepareSearchParamsForPage(pageNumber) {
      const { radius, method, name, zipOrCity } = this.searchParams
      const searchParams = {
        radius,
        pageNumber: pageNumber,
        _charset_: 'utf-8',
      }
      if (method) {
        searchParams.method = method
      }

      if (name) {
        searchParams.name = name
      }
      if (zipOrCity && zipOrCity.zipCode) {
        searchParams.zipOrCity = zipOrCity.zipCode
      }
      return searchParams
    },
    userDidPressCancel() {
      this.searchParams = {
        method: undefined,
        name: undefined,
        radius: this.distanceFieldItems[0].value,
        zipOrCity: undefined,
      }
      this.userHasSearched = false
      this.showErrorMessage = false
      this.results.length = 0
    },
    async getLocation(type, query) {
      return client('api')
        .get('masterdata/location-search', { q: encodeURI(query) })
        .then(locations =>
          locations.map(l =>
            Object.assign({}, l, {
              zipCodeAddOn: l.zipCodeAddOn || l.zipCodeAddon,
            }),
          ),
        )
    },
  },
}
</script>

<style lang="postcss" scoped>
.therapist-search {
  display: block;
  border-top: 1px solid var(--c-secondary-neutral-3);
  border-bottom: 1px dotted var(--c-secondary-neutral-3);
  padding: 40px 0;

  .is-hidden {
    display: none;
  }

  #list-date {
    @apply --polymer-paragraph-bold-secondary;
    margin: 0 0 30px 0;
  }

  #alert-text {
    margin-bottom: 17px;
  }

  .margin-bottom {
    margin-bottom: var(--spacing-6);
    display: block;
  }

  #error-text {
    @apply --polymer-paragraph-bold;
    color: var(--c-secondary-color-1);
  }

  #no-results-found-text {
    @apply --polymer-paragraph-s-terciary;
    margin: 45px 0;
  }

  #search-table {
    /**
     * usually we start from top to bottom with margins, but in this case is
     * better to do a margin top on the table as it might/might not be there
     */
    margin: 40px 0 20px 0;
  }
}
</style>
